import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { UserPlus, Users } from 'lucide-react';

import { DataTable, type DataTableColumn } from '@/ui/DataTable';
import { EstadoDaTela } from '@/ui/EstadoDaTela';
import { FilterBar, type FilterFieldConfig } from '@/ui/FilterBar';
import { PageHeader } from '@/ui/PageHeader';
import { StatusBadge } from '@/ui/StatusBadge';
import { Button } from '@/ui/base/button';
import { TAMANHO_PAGINA_PADRAO, tamanhoDePaginaValido } from '@/ui/tamanhoDePagina';

import type { ItemDaLista, SituacaoDoUsuario } from './contrato';
import { useListaDeUsuarios, useOpcoes, type FiltrosDaLista } from './dados';
import {
  ROTULO_DA_SITUACAO,
  TOM_DA_SITUACAO,
  data,
  dataEHora,
  rotuloDoSegundoFator,
  rotuloDosPerfis,
} from './rotulos';

/** Espera depois da última tecla: cada busca é uma consulta registrada (acesso a dado pessoal). */
const ESPERA_DA_BUSCA_MS = 400;

function useValorAssentado(valor: string, ms: number): string {
  const [assentado, setAssentado] = useState(valor);
  useEffect(() => {
    const tempo = window.setTimeout(() => setAssentado(valor), ms);
    return () => window.clearTimeout(tempo);
  }, [valor, ms]);
  return assentado;
}

/** Linha de apoio do convite, sob a situação: quando saiu, quem mandou, até quando vale. */
function linhaDoConvite(item: ItemDaLista): string | null {
  const c = item.convite;
  if (c && item.situacao === 'CONVITE_EM_EMISSAO') {
    return `Em emissão desde ${dataEHora(c.emEmissaoDesde)} · pode levar até 5 minutos`;
  }
  if (!c || (item.situacao !== 'CONVITE_PENDENTE' && item.situacao !== 'CONVITE_EXPIRADO')) {
    return null;
  }
  const quem = c.enviadoPor ? ` por ${c.enviadoPor}` : '';
  const prazo =
    item.situacao === 'CONVITE_EXPIRADO'
      ? `expirou em ${data(c.expiraEm)}`
      : `expira em ${data(c.expiraEm)}`;
  const enviado = c.enviadoEm ? `Enviado em ${data(c.enviadoEm)}` : 'Registrado';
  // O e-mail não chega à pessoa neste ambiente: a lista diz, não só a tela interna.
  const destino =
    item.entregaDoEmail === 'caixa-de-testes'
      ? ' · o e-mail vai para a caixa de testes, não para a pessoa'
      : item.entregaDoEmail === 'retido'
        ? ' · o e-mail não sai neste ambiente (envio desligado)'
        : '';
  return `${enviado}${quem} · ${prazo}${destino}`;
}

/**
 * `/admin/usuarios`: todas as pessoas com acesso, e quem foi convidado e ainda
 * não entrou, numa lista só. A linha inteira abre a tela interna (regra 9 de
 * interface); convidar é tela própria. Os filtros ficam no endereço, para
 * voltar da tela interna sem perder a busca.
 */
export function UsuariosListaPage() {
  const navigate = useNavigate();
  const [parametros, setParametros] = useSearchParams();
  const [busca, setBusca] = useState(parametros.get('busca') ?? '');
  const buscaAssentada = useValorAssentado(busca, ESPERA_DA_BUSCA_MS);

  const filtros: FiltrosDaLista = {
    busca: buscaAssentada,
    perfil: parametros.get('perfil') ?? '',
    situacao: parametros.get('situacao') ?? '',
    segundoFator: parametros.get('segundoFator') ?? '',
    pagina: Math.max(1, Number(parametros.get('pagina') ?? 1) || 1),
    tamanho: tamanhoDePaginaValido(parametros.get('tamanho') ?? TAMANHO_PAGINA_PADRAO),
  };

  function mudar(valores: Partial<Record<keyof FiltrosDaLista, string>>) {
    const proximo = new URLSearchParams(parametros);
    for (const [chave, valor] of Object.entries(valores)) {
      if (valor) proximo.set(chave, valor);
      else proximo.delete(chave);
    }
    setParametros(proximo, { replace: true });
  }

  useEffect(() => {
    if ((parametros.get('busca') ?? '') !== buscaAssentada)
      mudar({ busca: buscaAssentada, pagina: '' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buscaAssentada]);

  const opcoes = useOpcoes();
  const consulta = useListaDeUsuarios(filtros);
  const lista = consulta.data;

  const colunas: DataTableColumn<ItemDaLista>[] = useMemo(
    () => [
      {
        id: 'nome',
        header: 'Nome',
        cell: (r) => <span className="font-medium text-foreground">{r.nome}</span>,
      },
      {
        id: 'email',
        header: 'E-mail',
        cell: (r) => <span className="text-sm text-muted-foreground">{r.email}</span>,
      },
      {
        id: 'perfil',
        header: 'Perfil de acesso',
        cell: (r) => <span className="text-sm">{rotuloDosPerfis(r.perfis)}</span>,
      },
      {
        id: 'situacao',
        header: 'Situação',
        cell: (r) => {
          const apoio = linhaDoConvite(r);
          return (
            <div className="space-y-1">
              <StatusBadge
                status={TOM_DA_SITUACAO[r.situacao]}
                label={ROTULO_DA_SITUACAO[r.situacao]}
              />
              {apoio && <p className="text-xs text-muted-foreground">{apoio}</p>}
            </div>
          );
        },
      },
      {
        id: 'ultimo-acesso',
        header: 'Último acesso',
        cell: (r) => (
          <span className="text-sm text-muted-foreground">
            {r.ultimoAcesso ? dataEHora(r.ultimoAcesso) : 'Nunca entrou'}
          </span>
        ),
      },
      {
        id: 'segundo-fator',
        header: 'Segundo fator',
        cell: (r) => <span className="text-sm">{rotuloDoSegundoFator(r.segundoFator)}</span>,
      },
    ],
    [],
  );

  const campos: FilterFieldConfig[] = [
    {
      key: 'busca',
      label: 'Busca',
      type: 'search',
      placeholder: 'Nome ou e-mail',
      flex: true,
    },
    {
      key: 'perfil',
      label: 'Perfil de acesso',
      type: 'select',
      allOptionLabel: 'Todos',
      options: (opcoes.data?.perfis ?? []).map((p) => ({ value: p.codigo, label: p.rotulo })),
    },
    {
      key: 'situacao',
      label: 'Situação',
      type: 'select',
      allOptionLabel: 'Todas',
      options: (Object.keys(ROTULO_DA_SITUACAO) as SituacaoDoUsuario[]).map((s) => ({
        value: s,
        label: ROTULO_DA_SITUACAO[s],
      })),
    },
    {
      key: 'segundoFator',
      label: 'Segundo fator',
      type: 'select',
      allOptionLabel: 'Todos',
      options: [
        { value: 'aplicativo', label: 'Aplicativo autenticador' },
        { value: 'email', label: 'Código por e-mail' },
        { value: 'nenhum', label: 'Não cadastrado' },
      ],
    },
  ];

  return (
    <div className="min-w-0 space-y-6">
      <PageHeader
        title="Usuários"
        description="Quem tem acesso ao sistema, com qual perfil de acesso, e quem foi convidado."
      >
        <Button asChild>
          <Link to="/admin/usuarios/convidar">
            <UserPlus className="mr-2 h-4 w-4" aria-hidden="true" />
            Convidar pessoa
          </Link>
        </Button>
      </PageHeader>

      <FilterBar
        fields={campos}
        values={{
          busca,
          perfil: filtros.perfil,
          situacao: filtros.situacao,
          segundoFator: filtros.segundoFator,
        }}
        onChange={(chave, valor) => {
          if (chave === 'busca') setBusca(valor);
          else mudar({ [chave]: valor, pagina: '' });
        }}
        onClear={() => {
          setBusca('');
          setParametros(new URLSearchParams(), { replace: true });
        }}
      />

      <EstadoDaTela
        carregando={consulta.isPending}
        erro={consulta.error}
        aoTentarDeNovo={() => void consulta.refetch()}
        descricao="os usuários"
        esqueleto={{ colunas: 6 }}
      >
        <DataTable
          columns={colunas}
          data={lista?.itens ?? []}
          isLoading={consulta.isPlaceholderData}
          total={lista?.total ?? 0}
          page={filtros.pagina}
          perPage={filtros.tamanho}
          onPageChange={(pagina) => mudar({ pagina: String(pagina) })}
          onPerPageChange={(tamanho) => mudar({ tamanho: String(tamanho), pagina: '' })}
          onRowClick={(r) => navigate(`/admin/usuarios/${r.id}`)}
          ariaLabel="Usuários do sistema"
          emptyIcon={Users}
          emptyTitle="Nenhuma pessoa encontrada"
          emptyDescription="Ajuste os filtros ou convide uma pessoa."
        />
      </EstadoDaTela>
    </div>
  );
}

export default UsuariosListaPage;
